import api from './api';
import type { ApiResponse, PaginatedResponse } from '@/types';

// =============================================================================
// Types
// =============================================================================

export type WhatsAppAccountStatus = 'disconnected' | 'connecting' | 'connected' | 'qr_required';
export type WhatsAppProviderType =
  | 'cloud_api'
  | 'evolution'
  | 'waxum'
  /** @deprecated retired 2026-09 (whatsapp-web.js); kept one release so legacy API rows still typecheck. */
  | 'personal';

export type WhatsAppConnectionMethod = 'qr' | 'phone_code';
export type ConversationStatus = 'open' | 'pending' | 'resolved';
export type MessageDirection = 'incoming' | 'outgoing';
/**
 * `unsupported` is a real message from the contact that no adapter could open —
 * an envelope WhatsApp wraps (album, event, native-flow payload, undecryptable
 * blob). `content` is always null on those rows by design; the placeholder is
 * drawn by the bubble, never stored. Control traffic (delete/edit/settings) is
 * dropped in the backend mapper and never reaches the UI at all.
 */
export type MessageType = 'text' | 'image' | 'document' | 'audio' | 'video' | 'sticker' | 'location' | 'ptt' | 'template' | 'interactive' | 'unsupported';
export type MessageStatus = 'pending' | 'sent' | 'delivered' | 'read' | 'failed';

export type WhatsAppHistorySyncStatus = 'idle' | 'syncing' | 'completed' | 'failed';

/**
 * QR-paired providers link the number as a WhatsApp "linked device": they
 * pair via QR / pairing code, can import phone history, send voice notes and
 * have no 24-hour customer-care window. Cloud API is the only non-QR provider.
 */
export const isQrProvider = (type: WhatsAppProviderType | null | undefined): boolean =>
  type === 'evolution' || type === 'waxum';

/** Any `(key) => string` — next-intl's `useTranslations('common')` fits. */
export type ProviderLabelTranslator = (key: string) => string;

/**
 * One provider label map for every surface (account form select, inbox tabs,
 * settings list). Keys live under `common.whatsappProvider` so the helper works
 * from any namespace. T-72-01: literal keys per arm, never `t(dynamic)`.
 */
export const providerLabel = (
  type: WhatsAppProviderType | null | undefined,
  t: ProviderLabelTranslator
): string => {
  switch (type) {
    case 'cloud_api':
      return t('whatsappProvider.cloudApi');
    case 'evolution':
      return t('whatsappProvider.evolution');
    case 'waxum':
      return t('whatsappProvider.waxum');
    case 'personal':
      return t('whatsappProvider.personalLegacy');
    default:
      return t('whatsappProvider.unknown');
  }
};

/** Icon family for a provider: token-based Cloud API vs. everything QR-paired. */
export const providerIcon = (type: WhatsAppProviderType | null | undefined): 'api' | 'qr' =>
  type === 'cloud_api' ? 'api' : 'qr';

/**
 * The history-import progress block the backend serves on the account
 * resource, the QR poll and the status poll (one shape, three places — the
 * plan's §3.4 wire contract). `enabled` is the account's own opt-in, not the
 * clinic flag: an account can carry a finished import long after the flag was
 * turned back off.
 */
export interface WhatsAppHistorySync {
  enabled: boolean;
  status: WhatsAppHistorySyncStatus;
  /** 0-100, as reported by the gateway for the chunk it is draining. */
  progress: number;
  chats: number;
  messages: number;
  last_chunk_at: string | null;
  last_synced_at: string | null;
}

export interface WhatsAppAccountUser {
  id: string;
  full_name: string;
  email: string;
  is_default: boolean;
}

export interface WhatsAppAccount {
  id: string;
  name: string;
  phone_number: string | null;
  status: WhatsAppAccountStatus;
  provider_type: WhatsAppProviderType;
  qr_code: string | null;
  connected_at: string | null;
  disconnected_at: string | null;
  last_synced_at?: string | null;
  history_sync?: WhatsAppHistorySync;
  // Cloud API fields (only present for cloud_api accounts)
  cloud_api_phone_number_id?: string | null;
  cloud_api_business_account_id?: string | null;
  cloud_api_webhook_verify_token?: string | null;
  has_cloud_api_credentials?: boolean;
  users?: WhatsAppAccountUser[];
  created_at: string;
  updated_at: string;
}

export interface WhatsAppConnectionState {
  qr_code: string | null;
  pairing_code: string | null;
  pairing_code_generated_at: string | null;
  status: WhatsAppAccountStatus;
  /** Optional: served by backends that ship the history import. */
  history_sync?: WhatsAppHistorySync;
}

export interface WhatsAppConversation {
  id: string;
  // Historical conversations can outlive a soft-deleted WhatsApp account.
  account: Pick<WhatsAppAccount, 'id' | 'name' | 'phone_number'> | null;
  // Contact details are MASKED server-side for viewers without
  // `leads.view.phone`: `lead.phone` / `remote_phone` come back null and
  // `remote_jid` as `***@s.whatsapp.net`. Never render them raw and never
  // derive a number from `remote_jid` -- use formatRemotePhone() /
  // conversationTitle() below, and pass `leadId` to dialing controls so the
  // backend resolves the number.
  lead: {
    id: string;
    full_name: string;
    phone: string | null;
  } | null;
  assigned_to: { id: string; full_name: string } | null;
  lead_suggestions: Array<{ id: string; full_name: string; phone: string | null }> | null;
  remote_jid: string;
  is_group: boolean;
  remote_name: string | null;
  remote_phone: string | null;
  display_name: string;
  /**
   * WhatsApp showed this contact only by a privacy id (`@lid`): the CRM does
   * not know the number, for any viewer. Show a "number hidden by WhatsApp"
   * hint instead of a phone. Optional: older backends do not send it.
   */
  phone_unknown?: boolean;
  /** The account cannot reply to a hidden number (only waxum can). */
  reply_blocked?: boolean;
  status: ConversationStatus;
  unread_count: number;
  unread_from_message_id?: string | null;
  manual_unread_token?: string | null;
  last_message: WhatsAppMessage | null;
  last_message_at: string | null;
  last_customer_message_at: string | null;
  is_cloud_api: boolean;
  created_at: string;
  updated_at: string;
}

export interface WhatsAppMessageAuthor {
  jid: string;
  name: string | null;
  /** Null when masked (viewer lacks `leads.view.phone`). */
  phone: string | null;
}

/**
 * "+<digits>" for display, or null when the backend masked the number (or
 * WhatsApp hid it). Callers must fall back to a placeholder rather than print
 * "+null". Stored numbers already carry the "+" -- never double it.
 */
export function formatRemotePhone(phone: string | null | undefined): string | null {
  if (!phone) return null;
  return phone.startsWith('+') ? phone : `+${phone}`;
}

/**
 * Thread title: display_name -> remote_name -> "+phone". Null when every
 * candidate is empty or masked; the caller supplies the translated fallback.
 */
export function conversationTitle(
  conversation: Pick<WhatsAppConversation, 'display_name' | 'remote_name' | 'remote_phone'>
): string | null {
  return (
    conversation.display_name ||
    conversation.remote_name ||
    formatRemotePhone(conversation.remote_phone)
  );
}

/**
 * The quoted message rendered small enough for a reply strip. Content is
 * clipped server-side — never assume it is the full body.
 */
export interface WhatsAppQuotedMessage {
  id: string;
  whatsapp_message_id: string;
  direction: MessageDirection;
  type: MessageType;
  content: string | null;
  author_name: string | null;
  has_media: boolean;
}

/** Real coordinates from a shared location (the `location` column, not content). */
export interface WhatsAppMessageLocation {
  lat: number;
  lng: number;
  name?: string | null;
  address?: string | null;
}

/** A reaction folded onto the message it targets — reactions have no rows. */
export interface WhatsAppMessageReaction {
  emoji: string;
  author_jid: string | null;
  at: string;
}

/**
 * A chat EVENT the phone keeps as a message row — a call or a
 * delete-for-everyone — never something anybody typed. The row is stored as
 * `text` with an English `content` line so older UIs, the list preview and
 * exports still read; the bubble re-labels it from this block.
 */
export interface WhatsAppSystemEvent {
  kind: 'call' | 'deleted';
  /** `call_log` = the chat's own callLogMessage; `stub` = a WebMessageInfo stub type. */
  source: 'call_log' | 'stub';
  stub_type?: string;
  is_video?: boolean;
  /** CONNECTED / MISSED / REJECTED / FAILED / … as the phone reported it. */
  outcome?: string | null;
  duration_secs?: number | null;
}

export interface WhatsAppMessage {
  id: string;
  conversation_id: string;
  whatsapp_message_id: string;
  direction: MessageDirection;
  type: MessageType;
  content: string | null;
  author: WhatsAppMessageAuthor | null;
  media_url: string | null;
  media_mime_type: string | null;
  media_filename: string | null;
  media_size: number | null;
  has_media: boolean;
  sender: {
    id: string;
    full_name: string;
  } | null;
  status: MessageStatus;
  error_message: string | null;
  sent_at: string | null;
  // Phase 3 — receipt stamps (write-once; a 'read' receipt back-fills delivered_at).
  delivered_at?: string | null;
  read_at?: string | null;
  created_at: string;
  // Phase 3 — context WhatsApp attaches around the body.
  // reply_to_message_id is the QUOTED message's wamid; reply_to is the hydrated
  // snippet the bubble renders, absent when the quote predates the CRM.
  reply_to_message_id?: string | null;
  reply_to?: WhatsAppQuotedMessage | null;
  location?: WhatsAppMessageLocation | null;
  // One entry per reacting author (a second reaction from the same author
  // replaces the first; removing one drops the entry).
  reactions?: WhatsAppMessageReaction[];
  // Phase 46 — WhatsApp Reminder Management (Plan 02 adds origin/metadata columns).
  // Phase 56 D-15 — adds 'automation' literal so workflow-fired messages can be
  // distinguished in the inbox bubble (Plan 09 owns the 'automation' arm in
  // message-bubble.tsx). The metadata shape for origin === 'automation' is
  // documented as WhatsAppMessageAutomationMetadata in @/types.
  origin?: 'agent' | 'reminder' | 'system' | 'automation';
  metadata?: {
    template_meta_name?: string;
    language?: string;
    rendered_slots?: { body: Array<{ slot: number; value: string }> };
    rendered_text?: string;
    // Phase 56 — present when origin === 'automation' (D-16 snapshot).
    workflow_id?: string;
    workflow_name?: string;
    enrollment_id?: string;
    step_number?: number;
    total_steps?: number;
    rendered_body?: string;
    substituted_vars?: {
      first_name?: string | null;
      last_name?: string | null;
    };
    // Phase 3 — the contact tapped a quick-reply button / list row. `content`
    // holds the visible label; this is the id a workflow can branch on.
    button_reply_id?: string;
    button_reply_title?: string;
    // Phase 3 — reactions live here on the target message (mirrored to the
    // top-level `reactions` field by the resource).
    reactions?: WhatsAppMessageReaction[];
    // 2026-09-10 classification — what the provider called this message before
    // the CRM decided what it really was, and the gateway's own diagnosis of
    // the envelope it arrived in. Names only, never content.
    original_type?: string;
    type_coerced?: boolean;
    /** The wrapper chain the message arrived inside, outermost first. */
    wrapped_in?: string[];
    /** Proto fields the gateway saw but could not classify. */
    unknown_fields?: string[];
    /** Sent as view-once. The media is stored and shown like any other. */
    view_once?: boolean;
    /** Disappearing-message lifetime in seconds. */
    ephemeral_expiration?: number;
    /** 2026-09-23 — a call / delete event the phone keeps in the chat. */
    system_event?: WhatsAppSystemEvent | null;
    /** dentadent 260930 — set on a copy the CRM forwarded; the source row's id. */
    forwarded_from_message_id?: string;
  } | null;
}

/**
 * Which preview line a conversation list should show for a message.
 *
 * Pure, and deliberately NOT a translated string: the i18n rule here is literal
 * keys only, so the caller maps this to one of its own `t("…")` calls rather
 * than being handed a key to interpolate.
 *
 *  - `unsupported` — we know a message arrived and know we cannot render it;
 *  - `content`     — there is real text to show;
 *  - `media`       — an attachment with no caption;
 *  - `message`     — something arrived with neither, and we have nothing better.
 */
export function messagePreviewKind(
  message: Pick<WhatsAppMessage, 'type' | 'content'> & { has_media?: boolean; media_url?: string | null }
): 'unsupported' | 'content' | 'media' | 'message' {
  if (message.type === 'unsupported') return 'unsupported';
  if (message.content) return 'content';
  if (message.has_media || message.media_url) return 'media';
  return 'message';
}

export interface WhatsAppConversationNote {
  id: string;
  user: {
    id: string;
    full_name: string;
  };
  content: string;
  created_at: string;
}

// =============================================================================
// Request/Response Types
// =============================================================================

export interface CreateWhatsAppAccountData {
  name: string;
  provider_type?: WhatsAppProviderType;
  /** Import the chats already on the phone when this number is paired. */
  sync_history?: boolean;
  cloud_api_access_token?: string;
  cloud_api_phone_number_id?: string;
  cloud_api_business_account_id?: string;
  cloud_api_webhook_verify_token?: string;
}

export interface UpdateWhatsAppAccountData {
  name?: string;
  sync_history?: boolean;
  cloud_api_access_token?: string;
  cloud_api_phone_number_id?: string;
  cloud_api_business_account_id?: string;
  cloud_api_webhook_verify_token?: string;
}

export interface AssignUsersData {
  user_ids: string[];
}

export interface ConnectWhatsAppAccountData {
  method?: WhatsAppConnectionMethod;
  phone_number?: string;
}

export interface SendMessageData {
  content?: string;
  media_path?: string;
  media_filename?: string;
  media_mime_type?: string;
  // W3-H:reply — LOCAL id of the message being replied to; the backend resolves
  // it to the wire id (a wamid from the client is never trusted).
  reply_to_message_id?: string | null;
}

export interface ComposeConversationPayload {
  phone: string;
  name?: string;
  country_code?: string;
  content?: string;
  template_name?: string;
  language_code?: string;
  components?: Array<{
    type: string;
    parameters: Array<{ type: string; text?: string }>;
  }>;
}

export interface ComposeConversationResponse {
  conversation: WhatsAppConversation;
  message: WhatsAppMessage;
  multi_match: boolean;
  attached_lead: { id: string; first_name: string; last_name: string };
}

export interface ConversationFilters {
  account_id?: string;
  status?: ConversationStatus;
  unassigned?: boolean;
  search?: string;
  date_from?: string;    // 'YYYY-MM-DD'
  date_to?: string;      // 'YYYY-MM-DD'
  agent_id?: string;     // user ID
  has_unread?: boolean;
  assigned_to_me?: boolean;
  // Phase 48 INBOX-02 — gated on whatsapp.view_unassigned permission server-side.
  // Filters conversations with NO direct agent assignment (assigned_to IS NULL).
  // Distinct from `unassigned` which filters orphan conversations (lead_id IS NULL).
  without_agent?: boolean;
  page?: number;
  per_page?: number;
}

export interface WhatsAppStatusResponse {
  accounts: Array<{
    id: string;
    name: string;
    status: WhatsAppAccountStatus;
    phone_number: string | null;
    provider_type: WhatsAppProviderType;
    history_sync?: WhatsAppHistorySync;
  }>;
  /** Numbers that are down (latest row per phone, none connected); owners see their own, managers all. */
  disconnected_alerts?: Array<{
    id: string;
    name: string;
    phone_number: string | null;
    disconnected_at: string | null;
  }>;
  total_unread: number;
}

// =============================================================================
// Account Management (Admin)
// =============================================================================

export const getWhatsAppAccounts = async (): Promise<WhatsAppAccount[]> => {
  const response = await api.get<ApiResponse<WhatsAppAccount[]>>('/whatsapp/accounts');
  return response.data.data;
};

export const getWhatsAppAccount = async (id: string): Promise<WhatsAppAccount> => {
  const response = await api.get<ApiResponse<WhatsAppAccount>>(`/whatsapp/accounts/${id}`);
  return response.data.data;
};

export const createWhatsAppAccount = async (data: CreateWhatsAppAccountData): Promise<WhatsAppAccount> => {
  const response = await api.post<ApiResponse<WhatsAppAccount>>('/whatsapp/accounts', data);
  return response.data.data;
};

export const updateWhatsAppAccount = async (id: string, data: UpdateWhatsAppAccountData): Promise<WhatsAppAccount> => {
  const response = await api.put<ApiResponse<WhatsAppAccount>>(`/whatsapp/accounts/${id}`, data);
  return response.data.data;
};

export const deleteWhatsAppAccount = async (id: string): Promise<void> => {
  await api.delete(`/whatsapp/accounts/${id}`);
};

// Connection Management
export const connectWhatsAppAccount = async (
  id: string,
  data: ConnectWhatsAppAccountData = {},
): Promise<WhatsAppAccount> => {
  const response = await api.post<ApiResponse<WhatsAppAccount>>(`/whatsapp/accounts/${id}/connect`, data);
  return response.data.data;
};

export const disconnectWhatsAppAccount = async (id: string): Promise<{ message: string }> => {
  const response = await api.post<ApiResponse<{ message: string }>>(`/whatsapp/accounts/${id}/disconnect`);
  return response.data.data;
};

export const reconnectWhatsAppAccount = async (
  id: string,
  data: ConnectWhatsAppAccountData = {},
): Promise<WhatsAppAccount> => {
  const response = await api.post<ApiResponse<WhatsAppAccount>>(`/whatsapp/accounts/${id}/reconnect`, data);
  return response.data.data;
};

export const getAccountConnectionState = async (id: string): Promise<WhatsAppConnectionState> => {
  const response = await api.get<ApiResponse<WhatsAppConnectionState>>(`/whatsapp/accounts/${id}/qr`);
  return response.data.data;
};

export const getAccountQrCode = getAccountConnectionState;

// User Assignment
export const getAccountUsers = async (id: string): Promise<WhatsAppAccountUser[]> => {
  const response = await api.get<ApiResponse<WhatsAppAccountUser[]>>(`/whatsapp/accounts/${id}/users`);
  return response.data.data;
};

export const assignUsersToAccount = async (id: string, data: AssignUsersData): Promise<WhatsAppAccountUser[]> => {
  const response = await api.post<ApiResponse<WhatsAppAccountUser[]>>(`/whatsapp/accounts/${id}/users`, data);
  return response.data.data;
};

export const removeUserFromAccount = async (accountId: string, userId: string): Promise<void> => {
  await api.delete(`/whatsapp/accounts/${accountId}/users/${userId}`);
};

// =============================================================================
// Conversations
// =============================================================================

export const getConversations = async (filters: ConversationFilters = {}): Promise<{
  data: WhatsAppConversation[];
  meta: { current_page: number; per_page: number; total: number; last_page: number };
}> => {
  const params = new URLSearchParams();
  if (filters.account_id) params.append('account_id', filters.account_id);
  if (filters.status) params.append('status', filters.status);
  if (filters.unassigned) params.append('unassigned', 'true');
  if (filters.search) params.append('search', filters.search);
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);
  if (filters.agent_id) params.append('agent_id', filters.agent_id);
  if (filters.has_unread) params.append('has_unread', '1');
  if (filters.assigned_to_me) params.append('assigned_to_me', '1');
  if (filters.without_agent) params.append('without_agent', 'true');
  if (filters.page) params.append('page', String(filters.page));
  if (filters.per_page) params.append('per_page', String(filters.per_page));

  const response = await api.get<PaginatedResponse<WhatsAppConversation>>(`/whatsapp/conversations?${params}`);
  return { data: response.data.data, meta: response.data.meta };
};

export const getConversation = async (id: string): Promise<WhatsAppConversation> => {
  const response = await api.get<ApiResponse<WhatsAppConversation>>(`/whatsapp/conversations/${id}`);
  return response.data.data;
};

export const updateConversationStatus = async (id: string, status: ConversationStatus): Promise<WhatsAppConversation> => {
  const response = await api.patch<ApiResponse<WhatsAppConversation>>(`/whatsapp/conversations/${id}/status`, { status });
  return response.data.data;
};

export const assignConversationToLead = async (id: string, leadId: string): Promise<WhatsAppConversation> => {
  const response = await api.post<ApiResponse<WhatsAppConversation>>(`/whatsapp/conversations/${id}/assign`, { lead_id: leadId });
  return response.data.data;
};

export const assignConversationToAgent = async (id: string, userId: string | null): Promise<WhatsAppConversation> => {
  const response = await api.post<ApiResponse<WhatsAppConversation>>(`/whatsapp/conversations/${id}/assign-agent`, { user_id: userId });
  return response.data.data;
};

export const markConversationAsRead = async (id: string, manualUnreadToken?: string): Promise<WhatsAppConversation> => {
  const response = await api.post<ApiResponse<WhatsAppConversation>>(`/whatsapp/conversations/${id}/read`, {
    manual_unread_token: manualUnreadToken,
  });
  return response.data.data;
};

export const markMessageAsUnread = async (conversationId: string, messageId: string): Promise<WhatsAppConversation> => {
  const response = await api.post<ApiResponse<WhatsAppConversation>>(
    `/whatsapp/conversations/${conversationId}/messages/${messageId}/unread`
  );
  return response.data.data;
};

export const getMessagePage = async (conversationId: string, messageId: string): Promise<number> => {
  const response = await api.get<ApiResponse<{ page: number }>>(
    `/whatsapp/conversations/${conversationId}/messages/${messageId}/position`
  );
  return response.data.data.page;
};

export const composeConversation = async (
  accountId: string,
  payload: ComposeConversationPayload
): Promise<ComposeConversationResponse> => {
  const response = await api.post<ComposeConversationResponse>(
    `/whatsapp/accounts/${accountId}/conversations`,
    payload
  );
  return response.data;
};

// =============================================================================
// Messages
// =============================================================================

/**
 * Phase 8a — `search` filters the thread server-side (FULLTEXT on mysql, LIKE
 * on sqlite). Omitted/blank means "the whole thread", so existing callers are
 * unaffected.
 */
export const getMessages = async (
  conversationId: string,
  page = 1,
  search?: string
): Promise<{
  data: WhatsAppMessage[];
  meta: { current_page: number; per_page: number; total: number; last_page: number };
}> => {
  const params = new URLSearchParams({ page: String(page) });
  if (search && search.trim() !== '') {
    params.set('search', search.trim());
  }
  const response = await api.get<PaginatedResponse<WhatsAppMessage>>(
    `/whatsapp/conversations/${conversationId}/messages?${params.toString()}`
  );
  return { data: response.data.data, meta: response.data.meta };
};

/**
 * Phase 66 D-25 — fetch a single WhatsApp message by id.
 * Used by SendInvoiceWhatsAppDialog for the 30s post-dispatch status poll.
 */
export const getWhatsAppMessage = async (id: string): Promise<WhatsAppMessage> => {
  const response = await api.get<ApiResponse<WhatsAppMessage>>(`/whatsapp/messages/${id}`);
  return response.data.data;
};

/**
 * dentadent 260930 — the types a forward can re-send. Mirrors
 * OutboundMessageComposer::FORWARDABLE_TYPES; the server refuses the rest.
 */
export const FORWARDABLE_MESSAGE_TYPES: readonly MessageType[] = ['text', 'image', 'video', 'audio', 'ptt', 'document'];

/** WhatsApp's own cap on chats per forward; the server enforces the same. */
export const FORWARD_MAX_TARGETS = 5;

/**
 * Whether the bubble should offer Forward at all. Optimistic `tmp-` rows do
 * not exist server-side yet, and a view-once photo is never forwardable.
 */
export function isForwardable(message: WhatsAppMessage): boolean {
  if (message.id.startsWith('tmp-')) return false;
  if (!FORWARDABLE_MESSAGE_TYPES.includes(message.type)) return false;
  if (message.metadata?.view_once) return false;
  if (message.type === 'text') return Boolean(message.content?.trim());
  return Boolean(message.media_url);
}

/** Queue a copy of one message on each chosen chat; returns the new rows. */
export const forwardMessage = async (messageId: string, conversationIds: string[]): Promise<WhatsAppMessage[]> => {
  const response = await api.post<ApiResponse<WhatsAppMessage[]>>(
    `/whatsapp/messages/${messageId}/forward`,
    { conversation_ids: conversationIds }
  );
  return response.data.data;
};

export const sendMessage = async (conversationId: string, data: SendMessageData): Promise<WhatsAppMessage> => {
  const response = await api.post<ApiResponse<WhatsAppMessage>>(
    `/whatsapp/conversations/${conversationId}/messages`,
    data
  );
  return response.data.data;
};

/**
 * Phase 6 — upload progress for the composer's slim progress bar. Called with
 * 0..100; browsers that cannot report `total` never call it (axios only emits a
 * percentage when the length is known), so the bar must have an indeterminate
 * fallback.
 */
export type UploadProgressHandler = (percent: number) => void;

/** Both attachment paths cap at the backend's 16 MB (`max:16384` KB). */
export const WHATSAPP_MEDIA_MAX_BYTES = 16 * 1024 * 1024;

export const sendMediaMessage = async (
  conversationId: string,
  file: File,
  caption?: string,
  // W3-H:reply — optional quoted-message id, same contract as sendMessage.
  replyToMessageId?: string | null,
  onUploadProgress?: UploadProgressHandler
): Promise<WhatsAppMessage> => {
  const formData = new FormData();
  formData.append('media', file);
  if (caption) formData.append('content', caption);
  // W3-H:reply
  if (replyToMessageId) formData.append('reply_to_message_id', replyToMessageId);

  const response = await api.post<ApiResponse<WhatsAppMessage>>(
    `/whatsapp/conversations/${conversationId}/messages`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onUploadProgress
        ? (event) => {
            if (!event.total) return;
            onUploadProgress(Math.round((event.loaded * 100) / event.total));
          }
        : undefined,
    }
  );
  return response.data.data;
};

/**
 * Phase 6 — send a recorded voice note as `type=ptt`.
 *
 * The blob rides the same multipart `media` field as any other attachment; only
 * the declared type differs, and the backend keeps 'ptt' instead of collapsing
 * the audio MIME to plain 'audio'. Providers that cannot do voice notes (Cloud
 * API strictness) degrade to plain audio server-side — the composer does not
 * branch on provider here (D9).
 */
export const sendVoiceMessage = async (
  conversationId: string,
  audio: Blob,
  filename = 'voice-note.webm',
  onUploadProgress?: UploadProgressHandler
): Promise<WhatsAppMessage> => {
  const formData = new FormData();
  formData.append('media', audio, filename);
  formData.append('type', 'ptt');

  const response = await api.post<ApiResponse<WhatsAppMessage>>(
    `/whatsapp/conversations/${conversationId}/messages`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onUploadProgress
        ? (event) => {
            if (!event.total) return;
            onUploadProgress(Math.round((event.loaded * 100) / event.total));
          }
        : undefined,
    }
  );
  return response.data.data;
};

/**
 * Phase 7 — re-queue a failed send. 422s when the row is not 'failed', so the
 * caller should only offer it on a failed bubble.
 */
export const retryMessage = async (messageId: string): Promise<WhatsAppMessage> => {
  const response = await api.post<ApiResponse<WhatsAppMessage>>(
    `/whatsapp/messages/${messageId}/retry`
  );
  return response.data.data;
};

export const getMediaUrl = (messageId: string): string => {
  return `${api.defaults.baseURL}/whatsapp/media/${messageId}`;
};

// =============================================================================
// Notes
// =============================================================================

export const getConversationNotes = async (conversationId: string): Promise<WhatsAppConversationNote[]> => {
  const response = await api.get<ApiResponse<WhatsAppConversationNote[]>>(
    `/whatsapp/conversations/${conversationId}/notes`
  );
  return response.data.data;
};

export const addConversationNote = async (conversationId: string, content: string): Promise<WhatsAppConversationNote> => {
  const response = await api.post<ApiResponse<WhatsAppConversationNote>>(
    `/whatsapp/conversations/${conversationId}/notes`,
    { content }
  );
  return response.data.data;
};

// =============================================================================
// Account Conversation Counts
// =============================================================================

export interface AccountConversationCounts {
  account_id: string;
  total_unread: number;
  open_count: number;
  pending_count: number;
  resolved_count: number;
  total_count: number;
}

export const getAccountConversationCounts = async (): Promise<AccountConversationCounts[]> => {
  const response = await api.get<ApiResponse<AccountConversationCounts[]>>(
    '/whatsapp/conversations/account-counts'
  );
  return response.data.data;
};

// =============================================================================
// Status & Unread Count
// =============================================================================

export const getWhatsAppStatus = async (): Promise<WhatsAppStatusResponse> => {
  const response = await api.get<ApiResponse<WhatsAppStatusResponse>>('/whatsapp/status');
  return response.data.data;
};

export const getUnreadCount = async (): Promise<{ count: number }> => {
  const response = await api.get<ApiResponse<{ count: number }>>('/whatsapp/unread-count');
  return response.data.data;
};

// =============================================================================
// Lead Integration
// =============================================================================

export const getLeadConversations = async (leadId: string): Promise<WhatsAppConversation[]> => {
  const response = await api.get<ApiResponse<WhatsAppConversation[]>>(`/leads/${leadId}/whatsapp`);
  return response.data.data;
};

export const sendMessageToLead = async (
  leadId: string,
  accountId: string,
  data: SendMessageData
): Promise<WhatsAppMessage> => {
  const response = await api.post<ApiResponse<WhatsAppMessage>>(
    `/leads/${leadId}/whatsapp/send`,
    { account_id: accountId, ...data }
  );
  return response.data.data;
};

// =============================================================================
// Cloud API Types
// =============================================================================

export interface SendTemplateData {
  template_name: string;
  language_code: string;
  components?: Array<{
    type: 'header' | 'body' | 'button';
    parameters?: Array<{
      type: 'text' | 'image' | 'document' | 'video';
      text?: string;
      image?: { id: string };
      document?: { id: string; filename: string };
    }>;
    sub_type?: string;
    index?: number;
  }>;
}

export interface SendInteractiveData {
  body: string;
  buttons: Array<{
    id: string;
    title: string;
  }>;
}

export interface MetaTemplate {
  name: string;
  language: string;
  status: string;
  category: string;
  components: Array<{
    type: string;
    text?: string;
    format?: string;
    buttons?: Array<{ type: string; text: string }>;
  }>;
}

export interface WindowStatus {
  is_open: boolean;
  last_customer_message_at: string | null;
  expires_at: string | null;
  is_cloud_api: boolean;
}

// =============================================================================
// Cloud API Functions
// =============================================================================

export const sendTemplateMessage = async (
  conversationId: string,
  data: SendTemplateData
): Promise<WhatsAppMessage> => {
  const response = await api.post<ApiResponse<WhatsAppMessage>>(
    `/whatsapp/conversations/${conversationId}/send-template`,
    data
  );
  return response.data.data;
};

export const sendInteractiveMessage = async (
  conversationId: string,
  data: SendInteractiveData
): Promise<WhatsAppMessage> => {
  const response = await api.post<ApiResponse<WhatsAppMessage>>(
    `/whatsapp/conversations/${conversationId}/send-interactive`,
    data
  );
  return response.data.data;
};

export const getMetaTemplates = async (accountId: string): Promise<MetaTemplate[]> => {
  const response = await api.get<ApiResponse<MetaTemplate[]>>(
    `/whatsapp/accounts/${accountId}/templates`
  );
  return response.data.data;
};

export const refreshMetaTemplates = async (accountId: string): Promise<MetaTemplate[]> => {
  const response = await api.post<ApiResponse<MetaTemplate[]>>(
    `/whatsapp/accounts/${accountId}/templates/refresh`
  );
  return response.data.data;
};

export const getWindowStatus = async (conversationId: string): Promise<WindowStatus> => {
  const response = await api.get<ApiResponse<WindowStatus>>(
    `/whatsapp/conversations/${conversationId}/window-status`
  );
  return response.data.data;
};

// =============================================================================
// Connection Testing
// =============================================================================

export interface TestConnectionResult {
  success: boolean;
  verified_name?: string;
  display_phone_number?: string;
  meta_error_code?: number;
  meta_error_type?: string;
  meta_error_message?: string;
}

export const testCloudApiConnection = async (data: {
  cloud_api_access_token: string;
  cloud_api_phone_number_id: string;
}): Promise<TestConnectionResult> => {
  try {
    const response = await api.post<ApiResponse<TestConnectionResult>>(
      '/whatsapp/accounts/test-connection',
      data
    );
    return response.data.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string; errors?: TestConnectionResult } } };
    if (err.response?.data?.errors) {
      return { success: false, ...err.response.data.errors };
    }
    return {
      success: false,
      meta_error_message: err.response?.data?.message || 'Connection test failed',
    };
  }
};
